import assert from 'assert';
import { sum } from 'lodash';
import type { PartForStateMachine as Part } from './types';
import { UploadProgress, type UploadProgressValues } from './UploadProgress';
import { isS3UrlExpired } from './utils';

class Store {
  /** The file to be uploaded */
  private file: File;

  /** Total number of parts to be uploaded */
  private totalParts: number = 0;

  /** Callback to call when progress is updated */
  private onProgress?: (progress: UploadProgressValues) => void;

  /** Public getter for totalParts */
  public get totalPartCount() {
    return this.totalParts;
  }

  /** List of all parts */
  private allParts: Map<number, Part> = new Map();

  /** List of part indices that are pending to be uploaded */
  private pendingParts: Part[] = [];

  /**
   * List of part indices that are actively being uploaded
   * (cannot be more than MAX_CONCURRENCY)
   */
  private activeParts: Part[] = [];

  /** List of part indices that have been uploaded */
  private completedParts: Part[] = [];

  /** The previous ratio of bytes sent to total bytes */
  private prevRatio: number | undefined;

  /** The previous sample of bytes sent and bytes per second */
  private prevSample:
    | {
        timestamp: number;
        bytesSent: number;
        bytesPerSecond: number;
      }
    | undefined;

  /**
   * Get a part by index as defined in Part, not the array index
   * @param partIndex - The index of the part to retrieve
   * @returns The part with the specified index
   * @throws Error if part is not found
   */
  public getPart(partIndex: number) {
    const part = this.allParts.get(partIndex);
    if (!part) {
      throw new Error(`getPart: Part with id ${partIndex} not found`);
    }
    return part;
  }

  /**
   * Update a part in the store
   * @param part - The part to update
   */
  public updatePart(part: Part) {
    this.allParts.set(part.index, part);
  }

  /**
   * Get the next adjacent parts that don't have a url yet or have expired URLs
   * @param numParts - The number of parts to get
   * @returns Array of parts that either don't have URLs or have expired URLs and are adjacent to each other
   */
  public getNextAdjacentPartsToFetch(numParts: number) {
    // go through allParts, find the first numParts parts that either don't have a url
    // or have an expired url
    const result: Part[] = [];
    const partsToCheck = [...this.allParts.values()].filter((part) => {
      return !part.url || (part.url && isS3UrlExpired(part.url));
    });

    partsToCheck.sort((a, b) => a.index - b.index);
    // loop through the remaining parts, adding them to the result until we have numParts
    // OR we've reached the end of the list
    // OR the part.index is not adjacent to the last part.index
    for (const part of partsToCheck) {
      if (result.length >= numParts) {
        break;
      }
      // Adjacency check
      if (
        result.length > 0 &&
        part.index !== result[result.length - 1].index + 1
      ) {
        break;
      }
      result.push(part);
    }
    return result;
  }

  /**
   * @param totalParts - The total number of parts to be uploaded
   */
  constructor(
    file: File,
    totalParts: number,
    onProgress?: (progress: UploadProgressValues) => void
  ) {
    this.file = file;
    this.totalParts = totalParts;
    this.onProgress = onProgress;
  }

  /**
   * Initialize the sampling of bytes sent and bytes per second
   */
  public initSampling() {
    this.prevSample = {
      timestamp: performance.now(),
      bytesSent: 0,
      bytesPerSecond: 0,
    };
  }

  /**
   * Set the parts to be uploaded and reset the pending parts.
   * This is used when the uploader is initialized or when the parts are
   * reset.
   * @param parts - The parts to be uploaded
   * @throws Error if parts length doesn't match total parts
   */
  public setParts(parts: Part[]) {
    assert(
      parts.length === this.totalParts,
      'Parts length must match total parts'
    );
    this.allParts = new Map(parts.map((p) => [p.index, p]));
    // only parts that have a url are pending
    this.pendingParts = [...this.allParts.values()].filter((part) => part.url);
  }

  /**
   * Reset the pending parts.
   * This is used when the uploader is paused.
   * @param parts - The parts to reset as pending
   */
  private resetPendingParts(parts: Part[]) {
    this.pendingParts = parts
      .map((p) => this.getPart(p.index))
      .filter((part): part is Part => part !== undefined);
    this.activeParts = [];
  }

  /**
   * Abort all non-complete parts and make these the new pending parts
   */
  public abortPendingParts() {
    const pendingParts = [...this.activeParts, ...this.pendingParts]
      .map((part) => {
        // Update the original part in allParts
        this.allParts.set(part.index, {
          ...part,
          bytesSent: 0,
        });
        return part;
      })
      .filter((part): part is Part => part !== undefined);
    this.resetPendingParts(pendingParts);
  }

  /**
   * Get the next part to be uploaded.
   * This is used when the part machine is looking for a part to upload.
   * @returns The next part to upload or undefined if no parts are pending
   */
  public getNextPart(): Part | undefined {
    if (this.pendingParts.length === 0) return undefined;
    const nextPart = this.pendingParts.shift();

    // If the url is expired, remove it from pendingParts and try again
    const isUrlExpired = nextPart?.url && isS3UrlExpired(nextPart.url);
    if (isUrlExpired) {
      nextPart.url = null;
      return this.getNextPart();
    }
    if (nextPart !== undefined) {
      this.activeParts.push(nextPart);
      return nextPart;
    }
    return undefined;
  }

  /**
   * Set the urls for a list of parts
   * @param parts - The parts to set the urls for
   * @param urls - The urls to set for the parts
   */
  public setPartUrls(parts: Part[], urls: string[]) {
    parts.forEach((part, index) => {
      this.allParts.set(part.index, {
        ...part,
        url: urls[index],
      });
    });

    // Add parts with URLs to pendingParts if they're not already there
    parts.forEach((part, index) => {
      if (urls[index]) {
        const updatedPart = this.allParts.get(part.index);
        if (updatedPart) {
          // Check if this part is already in pendingParts
          const alreadyPending = this.pendingParts.some(
            (p) => p.index === updatedPart.index
          );
          // Only add to pendingParts if it's not already in any of the part lists
          if (!alreadyPending) {
            this.pendingParts.push(updatedPart);
          }
        }
      }
    });
  }

  /**
   * Remove the url for a part
   * @param part - The part to remove the url for
   */
  public removePartUrl(part: Part) {
    this.allParts.set(part.index, { ...part, url: null });
  }

  /**
   * Complete a part.
   * This is used when the part machine has finished uploading a part.
   * @param part - The part that has been completed
   * @throws Error if part is not found in activeParts
   */
  public completePart(part: Pick<Part, 'index'>) {
    // move from activeParts to completedParts
    const completedIndex = this.activeParts.findIndex(
      (p) => p.index === part.index
    );
    if (completedIndex === -1) {
      throw new Error('completePart: Part not found in activeParts');
    }

    const activePart = this.activeParts[completedIndex];

    // Remove the part from activeParts
    this.activeParts.splice(completedIndex, 1);
    this.completedParts.push(activePart);
  }

  /**
   * Check if the uploader is complete.
   * This is used to determine if the uploader should be stopped.
   * @returns True if all parts have been uploaded
   */
  public isComplete() {
    return this.totalParts === this.completedParts.length;
  }

  /**
   * Get the state of the uploader.
   * @returns The current state of the uploader including all parts and progress
   */
  public getState() {
    return {
      pendingParts: [...this.pendingParts],
      activeParts: [...this.activeParts],
      completedParts: [...this.completedParts],
      allParts: [...this.allParts.values()],
      totalParts: this.totalParts,
      progress: this.getProgress(),
    };
  }

  /**
   * Get the progress of the uploader.
   * @returns The progress of the uploader
   */
  public getProgress() {
    return {
      pending: this.pendingParts.length,
      active: this.activeParts.length,
      completed: this.completedParts.length,
      summary: `${this.completedParts.length}/${this.totalParts}`,
    };
  }

  /**
   * Set the progress of a part. If enough time has passed, the progress callback
   * for the part will be called.
   * @param part - The part to set the progress for
   * @param progressEvent - The progress event to set for the part
   */
  public setPartProgress(
    part: Part,
    progressEvent: Pick<ProgressEvent, 'loaded'>
  ): void {
    // move from activeParts to completedParts
    const activePartIndex = this.activeParts.findIndex(
      (p) => p.index === part.index
    );
    if (activePartIndex === -1) {
      throw new Error('setPartProgress: Part not found in activeParts');
    }

    const activePart = this.activeParts[activePartIndex];
    activePart.bytesSent = progressEvent.loaded;

    // Update sent bytes for the part
    const bytesTotal = this.file.size;
    const bytesSent = sum([
      ...this.activeParts.map((p) => p.bytesSent),
      ...this.completedParts.map((p) => p.blob.size),
    ]);
    const ratio = bytesSent / bytesTotal;

    if (this.prevRatio && ratio - this.prevRatio < 0.01 && ratio !== 1) {
      return;
    }
    this.prevRatio = ratio;

    // Estimate upload speed.
    //
    // This is sampled at most once per `MIN_SAMPLE_INTERVAL` to even out
    // fluctuations that happen when parts report progress too close to each
    // other.
    if (!this.prevSample) return;
    const now = performance.now();
    const MIN_SAMPLE_INTERVAL = 2000;
    const elapsedTime = now - this.prevSample.timestamp;
    let { bytesPerSecond } = this.prevSample;
    if (!bytesPerSecond || elapsedTime >= MIN_SAMPLE_INTERVAL) {
      bytesPerSecond = Math.round(
        (bytesSent - this.prevSample.bytesSent) / (elapsedTime / 1000)
      );

      // Save current state for next update
      this.prevSample = {
        ...this.prevSample,
        timestamp: now,
        bytesSent,
        bytesPerSecond,
      };
      this.onProgress?.(this.getTotalUploadProgress().values);
    }

    const updatedPart = this.getPart(part.index);
    this.allParts.set(part.index, {
      ...updatedPart,
      ...activePart,
    });
  }

  /**
   * Get the total upload progress for all parts.
   * @returns The total upload progress
   */
  public getTotalUploadProgress() {
    const uploadProgress = new UploadProgress({
      bytesSent: this.prevSample?.bytesSent ?? 0,
      bytesTotal: this.file.size,
      bytesPerSecond: this.prevSample?.bytesPerSecond ?? 0,
    });
    return uploadProgress;
  }
}

export default Store;
